import React, { useState, useEffect, useCallback } from "react";
import styles from "./Filters.module.css";
import MultiSelectFilter from "../../../components/MultiSelectFilter/MultiSelectFilter";
import TextInputFilter from "../../../components/TextInputFilter/TextInputFilter";
import { useCountries } from "../../../hooks/useCountries";
import { useSubjects } from "../../../hooks/useSubjects";
import { useMentorship } from "../../../hooks/useMentorship";
import { AcademicStandingMentor, YearsExperience } from "../../../utils/constants";

export default function FiltersMentor({ role, onFilterChange, currentFilters }) {
  const [localFilters, setLocalFilters] = useState({
    regions: [],
    subjects: [],
    services: [],
    academicStanding: [],
    experienceLevel: [],
    hasPhd: false,
    hasPublications: false
  });
  const { countries, loading: countriesLoading } = useCountries();
  const { subjects, loading: subjectsLoading } = useSubjects();
  const { mentorshipOffered, loading: mentorshipLoading } = useMentorship();

  // Flatten mentorship data for Services filter
  const flattenedMentorshipServices = mentorshipOffered.flatMap(category => 
    (category.items || []).map(item => ({
      label: item.name || item.title,
      value: item.id || item._id
    }))
  );

  useEffect(() => {
    // Only sync searchExpert from parent, manage other filters locally
    setLocalFilters(prev => ({ 
      ...prev, 
      searchExpert: currentFilters.searchExpert || ""
    }));
  }, [currentFilters.searchExpert]);

  // Reset filters when role changes
  useEffect(() => {
    const resetFilters = {
      regions: [],
      subjects: [],
      services: [],
      academicStanding: [],
      experienceLevel: [],
      hasPhd: false,
      hasPublications: false
    };
    setLocalFilters(resetFilters);
    onFilterChange(resetFilters);
  }, [role]);


  // Convert country values back to country objects for display
  const getCountryObjects = (countryValues) => {
    if (!countryValues || !Array.isArray(countryValues) || countryValues.length === 0) {
      return [];
    }
    
    if (!countries || countries.length === 0) {
      return countryValues.map(value => ({ label: value, value }));
    }
    
    return countryValues.map(value => {
      const country = countries.find(country => country.value === value);
      return country || { label: value, value };
    });
  };

  // Convert subject values back to subject objects for display
  const getSubjectObjects = (subjectValues) => {
    if (!subjectValues || !Array.isArray(subjectValues) || subjectValues.length === 0) {
      return [];
    }
    
    if (!subjects || subjects.length === 0) {
      return subjectValues.map(value => ({ label: value, value }));
    }
    
    return subjectValues.map(value => {
      const subject = subjects.find(subject => subject.value === value);
      return subject || { label: value, value };
    });
  };

  // Convert filter values back to objects for display
  const getFilterObjects = (filterValues, filterOptions) => {
    if (!filterValues || !Array.isArray(filterValues) || filterValues.length === 0) {
      return [];
    }
    
    if (!filterOptions || filterOptions.length === 0) {
      return filterValues.map(value => ({ label: value, value }));
    }
    
    return filterValues.map(value => {
      const option = filterOptions.find(option => option.value === value);
      return option || { label: value, value };
    });
  };

  const handleMultiSelectChange = (filterKey, selectedItems) => {
    const updatedFilters = { ...localFilters, [filterKey]: selectedItems };
    setLocalFilters(updatedFilters);
    // Send combined filters to parent
    onFilterChange({ ...currentFilters, ...updatedFilters });
  };

  const handleCheckboxChange = (filterKey, value) => {
    const updatedFilters = { ...localFilters, [filterKey]: value };
    setLocalFilters(updatedFilters);
    // Send combined filters to parent
    onFilterChange({ ...currentFilters, ...updatedFilters });
  };

  const handleAcademicStandingTextApply = (value) => {
    // Convert text value to array format similar to previous implementation
    const academicStandingArray = value.trim() ? [value] : [];
    const updatedFilters = { ...localFilters, academicStanding: academicStandingArray };
    setLocalFilters(updatedFilters);
    // Send combined filters to parent
    onFilterChange({ ...currentFilters, ...updatedFilters });
  };

  const handleAcademicStandingTextClear = () => {
    const updatedFilters = { ...localFilters, academicStanding: [] };
    setLocalFilters(updatedFilters);
    // Send combined filters to parent
    onFilterChange({ ...currentFilters, ...updatedFilters });
  };

  const handleClearAllFilters = () => {
    const resetFilters = {
      regions: [],
      subjects: [],
      services: [],
      academicStanding: [],
      experienceLevel: [],
      hasPhd: false,
      hasPublications: false,
      searchExpert: ""
    };
    setLocalFilters(resetFilters);
    onFilterChange(resetFilters);
  };
  return (
    <div className={styles.filters}>
      <div className={styles.container}>
        <button className={styles.filterBtn}> Filters</button>
        <MultiSelectFilter
          dropdownTitle="Country"
          placeholder="Search Country..."
          fetchData={(query) =>
            Promise.resolve(
              countries.filter((country) =>
                country.label.toLowerCase().includes(query.toLowerCase())
              )
            )
          }
          loading={countriesLoading}
          selected={getCountryObjects(localFilters.regions)}
          onSelectionChange={(selected) =>
            handleMultiSelectChange("regions", selected)
          }
        />
        <MultiSelectFilter
          dropdownTitle="Subject Areas"
          placeholder="Search subject..."
          fetchData={(query) =>
            Promise.resolve(
              subjects.filter((s) =>
                s.label.toLowerCase().includes(query.toLowerCase())
              )
            )
          }
          loading={subjectsLoading}
          selected={getSubjectObjects(localFilters.subjects)}
          onSelectionChange={(selected) => handleMultiSelectChange('subjects', selected)}
        />

        <MultiSelectFilter
          dropdownTitle="Services"
          placeholder="Search services..."
          fetchData={(query) =>
            Promise.resolve(
              flattenedMentorshipServices.filter((s) =>
                s.label?.toLowerCase().includes(query.toLowerCase())
              )
            )
          }
          loading={mentorshipLoading}
          selected={getFilterObjects(localFilters.services, flattenedMentorshipServices)}
          onSelectionChange={(selected) => handleMultiSelectChange('services', selected)}
        />
      
        <TextInputFilter
          dropdownTitle="Academic Standing"
          placeholder="Search academic standing..."
          value={localFilters.academicStanding.length > 0 ? localFilters.academicStanding[0] : ""}
          onApply={handleAcademicStandingTextApply}
          onClear={handleAcademicStandingTextClear}
        />
        <MultiSelectFilter
          dropdownTitle="Experience Level"
          placeholder="Search experience level..."
          fetchData={(query) =>
            Promise.resolve(
              YearsExperience.filter((s) =>
                s.label.toLowerCase().includes(query.toLowerCase())
              )
            )
          }
          selected={getFilterObjects(localFilters.experienceLevel, YearsExperience)}
          onSelectionChange={(selected) => handleMultiSelectChange('experienceLevel', selected)}
        />
  
        {/* <label>
          <input
            type="checkbox"
            checked={localFilters.hasPhd}
            onChange={(e) => handleCheckboxChange('hasPhd', e.target.checked)}
          /> Only Ph.D.
        </label>
        <label>
          <input
            type="checkbox"
            checked={localFilters.hasPublications}
            onChange={(e) => handleCheckboxChange('hasPublications', e.target.checked)}
          /> Has Publications
        </label> */}
        <button 
          className={styles.clearBtn}
          onClick={handleClearAllFilters}
        >
          Clear Filters
        </button>
      </div>
    </div>
  );
}

